import { apiFetch } from "./client";
import type { LotOptions } from "./dispatchOrders";
import type { LotQualityStatus } from "../components/LotQualityBadge";

/**
 * Magacin: roba na stanju onako kako je broji ERP, po skladištu, magacinskom polju, artiklu i LOT-u.
 *
 * Portal stanje čita iz ERP-a na nekoliko minuta i drži snimak, pa ekran uvek kaže kada je stanje pročitano.
 * Polje čiji naziv završava na „-0" (11-0, FSH-0) je proizvodni magacin: tu realizacija knjiži gotov proizvod.
 */
export type StockRow = {
  warehouseId: number;
  warehouseName: string | null;
  storeName: string | null;
  fieldId: number;
  fieldName: string | null;
  production: boolean;
  erpProductId: string;
  productName: string | null;
  unit: string | null;
  erpLotId: string;
  lot: string | null;
  /** Portalov zapis LOT-a, za sledljivost; null dok ga uvoz ne donese. */
  goodsLotId: string | null;
  expiresOn: string | null;
  quantity: number;
  firstReceivedOn: string | null;
  lastMovedOn: string | null;
  /** Šta je kvalitet rekao o LOT-u; null dok LOT nije ocenjen (neobrađen). */
  qualityStatus: LotQualityStatus | null;
  qualityNote: string | null;
  qualityDecidedBy: string | null;
  qualityDecidedAt: string | null;
  /** Ceo LOT, gde god mu palete stoje: na stanju, rezervisano po porudžbinama, slobodno. */
  lotStock: number | null;
  lotReserved: number | null;
  lotFree: number | null;
  reservations: Reservation[];
};

/**
 * Roba koja se drži za kupca, po porudžbini: rezervacija (`RESERVATION`) ili LOT na nalogu za otpremu koji još nije
 * otišao (`DISPATCH_ORDER`). Kupac i porudžbina su null kad ih korisnik ne sme videti (tuđi kupac): tada se zna samo
 * koliko je držano.
 */
export type Reservation = {
  id: string | null;
  kind: "RESERVATION" | "DISPATCH_ORDER";
  orderId: string | null;
  orderNumber: string | null;
  companyName: string | null;
  quantity: number;
  note: string | null;
  createdBy: string | null;
  createdAt: string | null;
  dispatchOrderId: string | null;
  dispatchOrderNumber: string | null;
  mayCancel: boolean;
  /** Od rezervacije: koliko su otpremnice porudžbine iznele posle nje; rezervisano je `quantity` i ovo zajedno. */
  shipped: number | null;
};

/** Porudžbina za koju LOT može da se rezerviše: čeka robu i ima artikal LOT-a. */
export type ReservableOrder = {
  orderId: string;
  orderNumber: string;
  status: string;
  companyName: string | null;
  country: string | null;
  quantity: number | null;
  unit: string | null;
};

/** LOT-ovi (ne pozicije) po statusu kvaliteta, pod istim filterom bez samog statusa. */
export type StatusCounts = { open: number; conforming: number; domesticOnly: number; nonconforming: number };

/** Filter statusa: OPEN su LOT-ovi koje kvalitet još nije ocenio. */
export type StatusFilter = "OPEN" | LotQualityStatus;

export type LotStatusView = {
  goodsLotId: string;
  lot: string | null;
  status: LotQualityStatus;
  note: string | null;
  decidedBy: string | null;
  decidedAt: string | null;
};

export type FieldFacet = { id: number; name: string | null; store: string | null; positions: number; production: boolean };
export type WarehouseFacet = { id: number; name: string | null; fields: FieldFacet[] };
/** Cela količina artikla pod istim filterom, ne samo deo na ovoj strani. */
export type ProductTotal = { erpProductId: string; quantity: number; positions: number };

export type StockPage = {
  syncedAt: string | null;
  positions: number | null;
  /** Poslednje čitanje ERP-a nije uspelo posle ovog trenutka; prikazano stanje je starije nego što bi trebalo. */
  failedAt: string | null;
  warehouses: WarehouseFacet[];
  rows: StockRow[];
  productTotals: ProductTotal[];
  statusCounts: StatusCounts;
  total: number;
  page: number;
  size: number;
};

/**
 * LOT koji se drži za jednu porudžbinu: koliko ga ima na stanju i koliko se drži za nju (njena rezervacija ili LOT na
 * njenom nalogu koji još nije otišao, veće od ta dva), sa samo njenim držanjima.
 */
export type HeldLot = {
  goodsLotId: string;
  lot: string | null;
  erpProductId: string | null;
  expiresOn: string | null;
  qualityStatus: LotQualityStatus | null;
  qualityNote: string | null;
  stock: number;
  held: number;
  entries: Reservation[];
};

/**
 * Šta se drži za porudžbinu, da li korisnik sme da rezerviše još (porudžbina čeka robu, a kupac je njegov), i šta su
 * njene otpremnice već iznele, po ERP artiklu: otpremljeno i ono što se drži zajedno pokrivaju porudžbinu.
 */
export type OrderHolds = {
  orderId: string;
  mayReserve: boolean;
  domestic: boolean;
  lots: HeldLot[];
  shipped: Record<string, number>;
};

export type StockFilter = {
  warehouse?: number | null;
  field?: number | null;
  production?: boolean;
  status?: StatusFilter | null;
  q?: string;
  /** Tačno ovi artikli (šifre), za stranu proizvoda; pretraga po šifri bi našla i tuđi LOT koji je nosi u oznaci. */
  articles?: string[];
  page?: number;
  size?: number;
};

/** Jedan korak čitanja stanja iz ERP-a; `count` su pročitani redovi dok se čita, a upisane pozicije kad se upiše. */
export type StockRefreshStep = {
  step: "CONNECT" | "READ" | "SAVE";
  state: "WAITING" | "RUNNING" | "DONE" | "FAILED";
  count: number | null;
};

/** Čitanje stanja iz ERP-a koje je neko pokrenuo („Osveži stanje“), korak po korak. */
export type StockRefresh = {
  id: string;
  status: "RUNNING" | "SUCCESS" | "FAILED";
  startedAt: string;
  finishedAt: string | null;
  steps: StockRefreshStep[];
  positions: number | null;
  newLots: number | null;
  failure: string | null;
};

export const warehouseApi = {
  /** Pokreće čitanje stanja iz ERP-a odmah; ako jedno već traje, vraća to. */
  refreshStock: () => apiFetch<StockRefresh>("/warehouse/stock/refresh", { method: "POST" }),
  /** Poslednje pokrenuto čitanje kakvo je sada; prazno kad od podizanja servera nijedno nije pokrenuto. */
  stockRefresh: () => apiFetch<StockRefresh | undefined>("/warehouse/stock/refresh"),
  stock: (f: StockFilter = {}) => {
    const p = new URLSearchParams();
    if (f.warehouse != null) p.set("warehouse", String(f.warehouse));
    if (f.field != null) p.set("field", String(f.field));
    if (f.production) p.set("production", "true");
    if (f.status) p.set("status", f.status);
    if (f.q?.trim()) p.set("q", f.q.trim());
    for (const a of f.articles ?? []) p.append("article", a);
    if (f.page) p.set("page", String(f.page));
    if (f.size) p.set("size", String(f.size));
    const qs = p.toString();
    return apiFetch<StockPage>(`/warehouse/stock${qs ? `?${qs}` : ""}`);
  },
  reservableOrders: (goodsLotId: string, q?: string) =>
    apiFetch<ReservableOrder[]>(
      `/warehouse/reservable-orders?lot=${encodeURIComponent(goodsLotId)}${q?.trim() ? `&q=${encodeURIComponent(q.trim())}` : ""}`,
    ),
  reserve: (goodsLotId: string, orderId: string, quantity: number, note?: string | null) =>
    apiFetch<Reservation>(`/warehouse/lots/${goodsLotId}/reservations`, {
      method: "POST",
      body: { orderId, quantity, note: note ?? null },
    }),
  cancelReservation: (id: string) => apiFetch<void>(`/warehouse/reservations/${id}/cancel`, { method: "POST" }),
  /** Šta se drži za porudžbinu: njeni LOT-ovi, rezervacijom ili na nalogu. */
  orderHolds: (orderId: string) => apiFetch<OrderHolds>(`/warehouse/orders/${orderId}/holds`),
  /** LOT-ovi artikla porudžbine koje sme da drži, po roku; samo onome ko sme da rezerviše. */
  orderLotOptions: (orderId: string, article: string) =>
    apiFetch<LotOptions>(`/warehouse/orders/${orderId}/lot-options?article=${encodeURIComponent(article)}`),
  /** Grupna ocena: samo usaglašen, za više LOT-ova odjednom, sve ili ništa. Samo šef kvaliteta. */
  decideMany: (goodsLotIds: string[], status: LotQualityStatus) =>
    apiFetch<LotStatusView[]>(`/warehouse/lots/quality-status`, { method: "PUT", body: { goodsLotIds, status } }),
  /** Odluka kvaliteta o LOT-u; SS i neusaglašen traže razlog. Samo šef kvaliteta. */
  decide: (goodsLotId: string, status: LotQualityStatus, note?: string | null) =>
    apiFetch<LotStatusView>(`/warehouse/lots/${goodsLotId}/quality-status`, {
      method: "PUT",
      body: { status, note: note ?? null },
    }),
};
